// Figma: https://www.figma.com/design/iYlbYhvyJjh7X00UAcqCWt/About-page?node-id=13-5940
import { Box, Flex, Image, Link, Text } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import type { ReactNode } from "react";

function StyledLink({
  href,
  isExternal,
  accentLight,
  accentHex,
  children,
}: {
  href: string;
  isExternal?: boolean;
  accentLight: string;
  accentHex: string;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      isExternal={isExternal}
      color={accentLight}
      fontSize="18px"
      fontWeight="400"
      textDecoration="none"
      position="relative"
      display="inline-block"
      whiteSpace="nowrap"
      _hover={{ color: accentHex }}
      sx={{
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "0",
          left: "0",
          width: "0%",
          height: "1px",
          backgroundColor: accentHex,
          transition: "width 0.2s cubic-bezier(0.25, 0.1, 0.25, 1)",
        },
        "&:hover::after": {
          width: "100%",
        },
      }}
    >
      {children}
    </Link>
  );
}

interface ContactLink {
  text: string;
  href: string;
  isExternal?: boolean;
}

interface ContactCardProps {
  iconSrc: string;
  title: string;
  linkText: string;
  linkHref: string;
  isExternal?: boolean;
  /** Additional links shown after the primary link */
  extraLinks?: ContactLink[];
  /** Accent color as [r, g, b] — used for gradient glow, icon fill, and link color */
  accent?: [number, number, number];
  /** CSS filter to recolor the icon (needed because <img> can't inherit CSS vars) */
  iconFilter?: string;
  /** Optional inline icon component (replaces iconSrc when provided) */
  iconComponent?: ReactNode;
}

/* ── Stroke-drawing keyframes ── */
const drawPath = keyframes`
  from { stroke-dashoffset: 1.5; }
  to { stroke-dashoffset: 0; }
`;

const shineSpin = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 200% 0; }
`;

/** Shared SVG wrapper for animated icons */
function IconSvg({
  viewBox,
  className,
  children,
}: {
  viewBox: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Box
      as="svg"
      w="40px"
      h="40px"
      flexShrink={0}
      viewBox={viewBox}
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      overflow="visible"
      sx={{
        "& path": {
          strokeDasharray: 1.5,
          strokeDashoffset: 0,
        },
      }}
    >
      {children}
    </Box>
  );
}

export function SupportIconAnimated({ color }: { color: string }) {
  return (
    <IconSvg viewBox="0 0 22.8333 27.8787" className="icon-support">
      {/* Shield left half: top → down left → bottom */}
      <path
        className="draw-0"
        pathLength={1}
        d="M10.931 0.875842 L1.26436 6.24621 C0.946892 6.42258 0.75 6.7572 0.75 7.12037 V15.4627 C0.75 19.0944 2.71892 22.4406 5.89357 24.2043 L10.931 27.0029"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
      />
      {/* Shield right half: top → down right → bottom */}
      <path
        className="draw-0"
        pathLength={1}
        d="M10.931 0.875842 C11.233 0.708052 11.6003 0.708053 11.9023 0.875843 L21.569 6.24621 C21.8864 6.42258 22.0833 6.7572 22.0833 7.12037 V15.4628 C22.0833 19.0944 20.1144 22.4406 16.9398 24.2043 L11.9023 27.0029 C11.6003 27.1707 11.233 27.1707 10.931 27.0029"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
      />
      {/* Checkmark: draws last */}
      <path
        className="draw-2"
        pathLength={1}
        d="M8.08333 13.9394L10.75 16.606L16.0833 11.2727"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </IconSvg>
  );
}

export function CommunityIconAnimated({ color }: { color: string }) {
  return (
    <IconSvg viewBox="0 0 24 24" className="icon-community">
      {/* Bottom arc (center person body) — draws first */}
      <path
        className="draw-0"
        pathLength={1}
        d="M17 20C17 18.3431 14.7614 17 12 17C9.23858 17 7 18.3431 7 20"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Center head (circle) — draws second */}
      <path
        className="draw-1"
        pathLength={1}
        d="M12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11C15 12.6569 13.6569 14 12 14Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Left shoulder — left person draws third */}
      <path
        className="comm-left"
        pathLength={1}
        d="M3 16.0004C3 14.7702 4.2341 13.7129 6 13.25"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Left head */}
      <path
        className="comm-left"
        pathLength={1}
        d="M6 9.23611C5.38625 8.68679 5 7.8885 5 7C5 5.34315 6.34315 4 8 4C8.76835 4 9.46924 4.28885 10 4.76389"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Right shoulder — right person draws last */}
      <path
        className="comm-right"
        pathLength={1}
        d="M21 16.0004C21 14.7702 19.7659 13.7129 18 13.25"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Right head */}
      <path
        className="comm-right"
        pathLength={1}
        d="M18 9.23611C18.6137 8.68679 19 7.8885 19 7C19 5.34315 17.6569 4 16 4C15.2316 4 14.5308 4.28885 14 4.76389"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </IconSvg>
  );
}

export function BusinessIconAnimated({ color }: { color: string }) {
  return (
    <IconSvg viewBox="0 0 24 24" className="icon-business">
      {/* Graph line (zigzag — draws from bottom-left upward to top-right) */}
      <path
        className="draw-0"
        pathLength={1}
        d="M3 18L7.00078 13.9367C7.10532 13.8305 7.15771 13.7773 7.20461 13.7354C7.96397 13.0557 9.11271 13.0557 9.87207 13.7354C9.91908 13.7774 9.97163 13.8308 10.0767 13.9375C10.1817 14.0442 10.2352 14.0976 10.2822 14.1396C11.0416 14.8193 12.1899 14.8193 12.9492 14.1396C12.9962 14.0976 13.0493 14.0441 13.1543 13.9375L21.0005 6"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Arrow horizontal (draws left to right toward point) */}
      <path
        className="draw-1"
        pathLength={1}
        d="M15 6H18.0002H21.0005"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Arrow vertical (extends down from point) */}
      <path
        className="draw-1"
        pathLength={1}
        d="M21.0005 6L21 12"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </IconSvg>
  );
}

export function PressIconAnimated({ color }: { color: string }) {
  return (
    <IconSvg viewBox="0 0 24 24" className="icon-press">
      {/* 1. Top line (full width) */}
      <path
        className="press-0"
        pathLength={1}
        d="M3 4H21"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* 2. Image rectangle */}
      <path
        className="press-1"
        pathLength={1}
        d="M4.6 16H9.4C9.96005 16 10.2401 16 10.454 15.891C10.6422 15.7951 10.7951 15.6422 10.891 15.454C11 15.2401 11 14.9601 11 14.4V9.6C11 9.03995 11 8.75992 10.891 8.54601C10.7951 8.35785 10.6422 8.20487 10.454 8.10899C10.2401 8 9.96005 8 9.4 8H4.6C4.03995 8 3.75992 8 3.54601 8.10899C3.35785 8.20487 3.20487 8.35785 3.10899 8.54601C3 8.75992 3 9.03995 3 9.6V14.4C3 14.9601 3 15.2401 3.10899 15.454C3.20487 15.6422 3.35785 15.7951 3.54601 15.891C3.75992 16 4.03995 16 4.6 16Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* 3. Top text line (right of square) */}
      <path
        className="press-2"
        pathLength={1}
        d="M15 9.25H21"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* 4. Bottom text line (right of square) */}
      <path
        className="press-3"
        pathLength={1}
        d="M15 14.75H21"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* 5. Bottom line (full width) */}
      <path
        className="press-4"
        pathLength={1}
        d="M3 20H21"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </IconSvg>
  );
}

const DEFAULT_ACCENT: [number, number, number] = [255, 90, 44]; // blaze orange

export function ContactCard({
  iconSrc,
  title,
  linkText,
  linkHref,
  isExternal = false,
  extraLinks,
  accent = DEFAULT_ACCENT,
  iconFilter,
  iconComponent,
}: ContactCardProps) {
  const [r, g, b] = accent;
  const accentHex = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  const accentLight = `rgba(${r},${g},${b},0.85)`;

  return (
    <Flex
      gap="24px"
      align="flex-start"
      px="32px"
      py="40px"
      borderRadius="16px"
      border="1px solid"
      borderColor="rgba(255,255,255,0.10)"
      boxShadow="0 2px 8px rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.1)"
      transition="border-color 0.6s ease, box-shadow 0.6s ease"
      _hover={{
        borderColor: `rgba(${r},${g},${b},0.25)`,
        boxShadow: `0 4px 12px rgba(0,0,0,0.2), 0 12px 32px rgba(0,0,0,0.15), 0 0 24px rgba(${r},${g},${b},0.1)`,
      }}
      w="100%"
      position="relative"
      overflow="hidden"
      isolation="isolate"
      sx={{
        background: `radial-gradient(ellipse at top right, rgba(${r},${g},${b},0.1) 0%, rgba(${r},${g},${b},0) 70%), var(--chakra-colors-charcoal-600)`,
        /* Diagonal stripe pattern — fades from top-right */
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "16px",
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 8px,
            rgba(${r},${g},${b},0.1) 8px,
            rgba(${r},${g},${b},0.1) 9px
          )`,
          WebkitMaskImage:
            "radial-gradient(ellipse at top right, white 0%, transparent 60%)",
          maskImage:
            "radial-gradient(ellipse at top right, white 0%, transparent 60%)",
          pointerEvents: "none",
          zIndex: 0,
        },
        /* Magic UI shine border — masked radial gradient */
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "16px",
          padding: "1px",
          background: `radial-gradient(transparent, transparent, rgba(${r},${g},${b},0.7), transparent, transparent)`,
          backgroundSize: "200% 200%",
          WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          opacity: 0,
          transition: "opacity 0.6s ease",
          pointerEvents: "none",
        },
        "&:hover .inset-shadow": {
          boxShadow: "inset 0 0 40px rgba(0,0,0,0.6)",
        },
        "&:hover::before": {
          opacity: 1,
          animation: `${shineSpin} 6s linear infinite`,
        },
        /* Support: shield draws, then checkmark */
        "&:hover .icon-support .draw-0": {
          animation: `${drawPath} 0.8s ease both`,
        },
        "&:hover .icon-support .draw-2": {
          animation: `${drawPath} 0.4s ease 0.6s both`,
        },
        /* Community: arc → center head → left person → right person */
        "&:hover .icon-community .draw-0": {
          animation: `${drawPath} 0.4s ease both`,
        },
        "&:hover .icon-community .draw-1": {
          animation: `${drawPath} 0.35s ease 0.3s both`,
        },
        "&:hover .icon-community .comm-left": {
          animation: `${drawPath} 0.3s ease 0.5s both`,
        },
        "&:hover .icon-community .comm-right": {
          animation: `${drawPath} 0.3s ease 0.65s both`,
        },
        /* Press: top line → square → right lines → bottom line */
        "&:hover .icon-press .press-0": {
          animation: `${drawPath} 0.3s ease both`,
        },
        "&:hover .icon-press .press-1": {
          animation: `${drawPath} 0.45s ease 0.2s both`,
        },
        "&:hover .icon-press .press-2": {
          animation: `${drawPath} 0.2s ease 0.5s both`,
        },
        "&:hover .icon-press .press-3": {
          animation: `${drawPath} 0.2s ease 0.6s both`,
        },
        "&:hover .icon-press .press-4": {
          animation: `${drawPath} 0.3s ease 0.7s both`,
        },
        /* Business: graph climbs up, then arrow extends */
        "&:hover .icon-business .draw-0": {
          animation: `${drawPath} 0.8s ease both`,
        },
        "&:hover .icon-business .draw-1": {
          animation: `${drawPath} 0.4s ease 0.6s both`,
        },
      }}
    >
      {/* Inset shadow — on top of stripes, behind content */}
      <Box
        position="absolute"
        inset="0"
        borderRadius="16px"
        boxShadow="inset 0 0 40px rgba(0,0,0,0)"
        transition="box-shadow 0.6s ease"
        pointerEvents="none"
        zIndex={1}
        className="inset-shadow"
      />

      {/* Icon (13:5618) */}
      {iconComponent ?? (
        <Image src={iconSrc} alt="" w="40px" h="40px" flexShrink={0} filter={iconFilter} />
      )}

      {/* Text content (13:5617) */}
      <Flex direction="column" gap="8px" minW={0} flex={1}>
        {/* Title (13:5611) */}
        <Text fontSize="24px" fontWeight="400" color="dune.dune" lineHeight="1.4">
          {title}
        </Text>

        {/* Links (13:5615) */}
        <Flex
          align="center"
          gap={{ base: "0px", md: "12px" }}
          direction={{ base: "column", md: "row" }}
          alignItems={{ base: "flex-start", md: "center" }}
        >
          <StyledLink href={linkHref} isExternal={isExternal} accentLight={accentLight} accentHex={accentHex}>
            {linkText}
          </StyledLink>
          {extraLinks?.map((link) => (
            <Flex key={link.href} align="center" gap="12px">
              <Box
                as="span"
                w="4px"
                h="4px"
                borderRadius="full"
                bg="whiteAlpha.400"
                flexShrink={0}
                display={{ base: "none", md: "block" }}
              />
              <StyledLink href={link.href} isExternal={link.isExternal} accentLight={accentLight} accentHex={accentHex}>
                {link.text}
              </StyledLink>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
}
